'use client';
// The gold line at the top of the page while the next page loads. It starts on a click on a link to another page
// of the site (or on startRouteProgress(), for buttons that navigate) and finishes when the URL changes. A page
// that comes straight from the cache shows it for a blink; a slow one keeps it moving, so a click never looks lost.
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { isPageNavigation } from '@/lib/v9/routeProgress';

const START_EVENT = 'mila:route-start';
/** For navigations that are not a link click (router.push from a button). */
export function startRouteProgress() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(START_EVENT));
}

// Never stuck: a navigation that does not happen (a link whose handler did something else, a failed fetch) ends the
// line by itself.
const GIVE_UP_MS = 8_000;

export default function RouteProgress() {
  const pathname = usePathname();
  const search = useSearchParams();
  const [phase, setPhase] = useState<'idle' | 'running' | 'done'>('idle');
  const giveUp = useRef<number | null>(null);

  // Start. The listener is on the window, in the bubble phase: React's handlers run on the document (the app router
  // hydrates the whole document), so a control inside a link that stops the click (the photo zoom on a bundle card)
  // never lets it reach here, and next/link's own handler has already run.
  useEffect(() => {
    const start = () => {
      if (giveUp.current) window.clearTimeout(giveUp.current);
      giveUp.current = window.setTimeout(() => setPhase('done'), GIVE_UP_MS);
      setPhase('running');
    };
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      if (isPageNavigation({ href: a.href, target: a.target, download: a.hasAttribute('download') }, e, window.location.href)) start();
    };
    window.addEventListener('click', onClick);
    window.addEventListener(START_EVENT, start);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener(START_EVENT, start);
      if (giveUp.current) window.clearTimeout(giveUp.current);
    };
  }, []);

  // Finish: the URL changed.
  const key = `${pathname}?${search}`;
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    setPhase((p) => (p === 'running' ? 'done' : p));
  }, [key]);

  // Done: the line fills, fades, and goes.
  useEffect(() => {
    if (phase !== 'done') return;
    if (giveUp.current) { window.clearTimeout(giveUp.current); giveUp.current = null; }
    const id = window.setTimeout(() => setPhase('idle'), 450);
    return () => window.clearTimeout(id);
  }, [phase]);

  if (phase === 'idle') return null;
  return (
    <div role="progressbar" aria-busy={phase === 'running'} aria-valuetext={phase === 'running' ? 'Loading' : 'Loaded'}
      className="pointer-events-none fixed inset-x-0 top-0 z-[120] h-[3px]">
      <span
        className="absolute inset-y-0 start-0 block rounded-e-full bg-[linear-gradient(90deg,#D9B45F,#F0D58C_70%,#FFF4D0)] shadow-[0_0_12px_rgba(240,213,140,.95),0_1px_0_rgba(85,39,109,.3)] motion-reduce:transition-none"
        style={{
          width: phase === 'running' ? '82%' : '100%',
          opacity: phase === 'running' ? 1 : 0,
          // The line hurries at first and crawls near the end: it reads as "still loading", never as stalled.
          transition: phase === 'running'
            ? 'width 9s cubic-bezier(.05,.9,.25,1)'
            : 'width .2s ease-out, opacity .35s ease-out .15s',
        }}
      />
    </div>
  );
}
